<template>
	<v-container fluid>
		<v-row justify="center" align="start" class="mt-4">
			<v-col cols="12" md="4">
				<v-text-field
					v-model="policyNumber"
					label="Policy Number"
					variant="outlined"
					density="comfortable"
					@keyup.enter="getTrackers"
				></v-text-field>
			</v-col>
			<v-col cols="auto">
				<v-btn
					color="primary"
					@click="getTrackers"
					:loading="loading"
					:disabled="!policyNumber"
				>
					Get Tracker
				</v-btn>
			</v-col>
		</v-row>

		<v-row v-if="trackers.length > 0" justify="center" align="start">
			<v-col cols="12" md="6">
				<v-select
					v-model="selectedTrackerId"
					:items="trackerOptions"
					item-title="text"
					item-value="value"
					label="Select Form Tracker"
					variant="outlined"
					density="comfortable"
				></v-select>
			</v-col>
		</v-row>

		<!-- Tracker Details -->
		<v-row v-if="selectedTracker" justify="center">
			<v-col cols="12" md="9">
				<v-card>
					<v-card-text>
						<v-row>
							<!-- Column 1 -->
							<v-col cols="12" md="4">
								<div class="mb-3">
									<div class="text-caption text-medium-emphasis">ID</div>
									<div class="text-body-1">{{ selectedTracker.id }}</div>
								</div>
								<div class="mb-3">
									<div class="text-caption text-medium-emphasis">Policy Number</div>
									<div class="text-body-1">{{ selectedTracker.policyNumber }}</div>
								</div>
								<div class="mb-3">
									<div class="text-caption text-medium-emphasis">Amendment Number</div>
									<div class="text-body-1">{{ selectedTracker.amendmentNumber ?? 'N/A' }}</div>
								</div>
								<div class="mb-3">
									<div class="text-caption text-medium-emphasis">Date Started</div>
									<div class="text-body-1">{{ formatDateTime(selectedTracker.dateStarted) }}</div>
								</div>
							</v-col>
							<!-- Column 2 -->
							<v-col cols="12" md="4">
								<div class="mb-3">
									<div class="text-caption text-medium-emphasis">Date Finished</div>
									<div class="text-body-1">{{ selectedTracker.dateFinished ? formatDateTime(selectedTracker.dateFinished) : 'N/A' }}</div>
								</div>
								<div class="mb-3">
									<div class="text-caption text-medium-emphasis">Has Interactive Forms</div>
									<div class="text-body-1">{{ selectedTracker.hasInteractiveForms }}</div>
								</div>
								<div class="mb-3">
									<div class="text-caption text-medium-emphasis">Interactive Forms Generated</div>
									<div class="text-body-1">{{ selectedTracker.interactiveFormsGenerated }}</div>
								</div>
								<div class="mb-3">
									<div class="text-caption text-medium-emphasis">Has Policy Forms</div>
									<div class="text-body-1">{{ selectedTracker.hasPolicyForms }}</div>
								</div>
							</v-col>
							<!-- Column 3 -->
							<v-col cols="12" md="4">
								<div class="mb-3">
									<div class="text-caption text-medium-emphasis">Policy Forms Generated</div>
									<div class="text-body-1">{{ selectedTracker.policyFormsGenerated }}</div>
								</div>
								<div class="mb-3">
									<div class="text-caption text-medium-emphasis">Document Rules Executed</div>
									<div class="text-body-1">{{ selectedTracker.documentRulesExecuted }}</div>
								</div>
								<div class="mb-3">
									<div class="text-caption text-medium-emphasis">Documents Blended</div>
									<div class="text-body-1">{{ selectedTracker.documentsBlended }}</div>
								</div>
								<div class="mb-3">
									<div class="text-caption text-medium-emphasis">Is Completed</div>
									<div class="text-body-1">{{ selectedTracker.isCompleted }}</div>
								</div>
							</v-col>
						</v-row>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>

		<!-- Policy Forms Table -->
		<v-row v-if="selectedTracker && selectedTracker.policyForms?.length > 0" justify="center" class="mt-4">
			<v-col cols="12" md="9">
				<v-card>
					<v-card-title>Policy Forms ({{ selectedTracker.policyForms.length }})</v-card-title>
					<v-card-text>
						<v-data-table
							:headers="policyFormsHeaders"
							:items="selectedTracker.policyForms"
							:expanded="expandedForms"
							item-value="formCode"
							show-expand
							density="comfortable"
						>
							<template #[`item.formGenerationStatus`]="{ item }">
								<v-chip
									:color="item.formGenerationStatus?.errorMessage ? 'error' : 'success'"
									size="small"
								>
									{{ item.formGenerationStatus?.errorMessage ? 'Error' : 'Success' }}
								</v-chip>
							</template>
							<template #expanded-row="{ columns, item }">
								<tr>
									<td :colspan="columns.length" class="pa-4 bg-grey-lighten-4">
										<v-row>
											<v-col cols="12" md="4">
												<div class="mb-2">
													<div class="text-caption font-weight-bold text-primary">GhostDraft Company</div>
													<div class="text-body-2">{{ item.ghostDraftCompany ?? 'N/A' }}</div>
												</div>
												<div class="mb-2">
													<div class="text-caption font-weight-bold text-primary">Document Service Name</div>
													<div class="text-body-2">{{ item.ghostDraftDocumentServiceName ?? 'N/A' }}</div>
												</div>
												<div class="mb-2">
													<div class="text-caption font-weight-bold text-primary">Package Name</div>
													<div class="text-body-2">{{ item.ghostDraftPackageName ?? 'N/A' }}</div>
												</div>
											</v-col>
											<v-col cols="12" md="4">
												<div class="mb-2">
													<div class="text-caption font-weight-bold text-primary">Package Version</div>
													<div class="text-body-2">{{ item.ghostDraftPackageVersion ?? 'N/A' }}</div>
												</div>
												<div class="mb-2">
													<div class="text-caption font-weight-bold text-primary">GhostDraft Form Name</div>
													<div class="text-body-2">{{ item.ghostDraftFormName ?? 'N/A' }}</div>
												</div>
												<div class="mb-2">
													<div class="text-caption font-weight-bold text-primary">Is Static</div>
													<div class="text-body-2">{{ item.isStatic }}</div>
												</div>
											</v-col>
											<v-col cols="12" md="4">
												<div class="mb-2">
													<div class="text-caption font-weight-bold text-primary">Recipients</div>
													<div class="text-body-2">{{ item.recipients?.join(', ') ?? 'N/A' }}</div>
												</div>
												<div class="mb-2">
													<div class="text-caption font-weight-bold text-primary">Transaction Types</div>
													<div class="text-body-2">{{ item.transactionTypes?.join(', ') ?? 'N/A' }}</div>
												</div>
												<div class="mb-2">
													<div class="text-caption font-weight-bold text-primary">Action Codes</div>
													<div class="text-body-2">{{ item.actionCodes?.join(', ') ?? 'N/A' }}</div>
												</div>
												<div v-if="item.formGenerationStatus?.errorMessage" class="mb-2">
													<div class="text-caption font-weight-bold text-error">Error Message</div>
													<div class="text-body-2 text-error">{{ item.formGenerationStatus.errorMessage }}</div>
												</div>
											</v-col>
										</v-row>
									</td>
								</tr>
							</template>
						</v-data-table>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>
	</v-container>
</template>

<script lang="ts">
import { ref, inject, computed } from 'vue';
import { useCommercialApi } from '@/composables/UseCommercialApi';
import { CommercialApiConfig } from '@/services/CommercialApiConfig';
import { PolicyFormsTracker, PolicyFormDetails } from '@/models/PrintDocumentModel';
import { Tell } from '@/services/Tell';
import { useDisplayFormatters } from '@/composables/UseDisplayFormatters';

export default {
	name: 'PolicyFormTrackers',
	route: {
		path: '/policy-form-trackers',
		meta: {
			requiresAuth: true,
			title: 'Policy Form Trackers'
		}
	},
	setup() {
		const policyNumber = ref<string>('');
		const trackers = ref<PolicyFormsTracker[]>([]);
		const selectedTrackerId = ref<string>('');
		const { getPolicyFormTrackers, loading } = useCommercialApi(CommercialApiConfig);
		const tell = inject('tell') as Tell;
		const { formatDateTime } = useDisplayFormatters();


		const trackerOptions = computed(() => {
			return trackers.value
				.map(tracker => {
					const displayDate = tracker.dateFinished || tracker.dateStarted;
					return {
						text: `${tracker.policyNumber} - ${formatDateTime(displayDate)}`,
						value: tracker.id,
						date: new Date(displayDate)
					};
				})
				.sort((a, b) => b.date.getTime() - a.date.getTime());
		});

		const selectedTracker = computed(() => {
			return trackers.value.find(t => t.id === selectedTrackerId.value) || null;
		});

		const expandedForms = ref<string[]>([]);

		const policyFormsHeaders = [
			{ title: 'Form Code', key: 'formCode', align: 'center' },
			{ title: 'Form Name', key: 'formName', align: 'center' },
			{ title: 'Edition Date', key: 'formEditionDate', align: 'center' },
			{ title: 'Form System', key: 'formSystem', align: 'center' },
			{ title: 'Interactive', key: 'isInteractive', align: 'center' },
			{ title: 'Status', key: 'formGenerationStatus', align: 'center' },
		];

		const getTrackers = async () => {
			if (!policyNumber.value) return;

			const result = await getPolicyFormTrackers<PolicyFormsTracker[]>(policyNumber.value);
			
			if (result.success && result.data) {
				trackers.value = result.data;
				// Auto-select the latest tracker (first in sorted list)
				if (trackerOptions.value.length > 0) {
					selectedTrackerId.value = trackerOptions.value[0].value;
				}
			} else {
				tell.error('Failed to fetch form trackers');
			}
		};

		return {
			policyNumber,
			trackers,
			selectedTrackerId,
			trackerOptions,
			selectedTracker,
			expandedForms,
			policyFormsHeaders,
			loading,
			getTrackers,
			formatDateTime
		};
	}
};
</script>

<style scoped>
</style>
